import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import axios from 'axios';
import './Sidebar.css';
import { FaHome, FaRegCalendarAlt, FaBars, FaCog, FaSignOutAlt, FaRegClone } from 'react-icons/fa';
import PeopleIcon from '@mui/icons-material/People';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import { capitalizeFirst } from '../../utils/functions';
import { useNotification } from '../notifications/useNotification';

const Sidebar = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();  // Hook to get the current URL path
    const { showError } = useNotification();
    const [isExpanded, setIsExpanded] = useState(false);
    const [activeItem, setActiveItem] = useState('home'); // Track which item is active
    const { setDietist } = useAppContext();

    // Update activeItem based on current URL
    useEffect(() => {
        const currentPath = location.pathname;
        if (currentPath.includes('home')) {
            setActiveItem('home');
        } else if (currentPath.includes('calendar')) {
            setActiveItem('calendar');
        } else if (currentPath.includes('clients') || currentPath.includes('client/')) {
            setActiveItem('clients');
        } else if (currentPath.includes('templates')) {
            setActiveItem('templates');
        } else if (currentPath.includes('foods') || currentPath.includes('recipes')) {
            setActiveItem('foods');
        } else if (currentPath.includes('settings')) {
            setActiveItem('settings');
        }
    }, [location.pathname]); // Depend on the pathname so that it updates when the URL changes

    // Function to handle when an item is clicked
    const handleMenuClick = (item) => {
        setActiveItem(item);
        handleNavigation(item);
    };

    const handleNavigation = (page) => {
        switch (page) {
            case "home":
                navigate("/home");
                break;
            case "calendar":
                navigate("/calendar");
                break;
            case "clients":
                navigate("/clients");
                break;
            case "templates":
                navigate('/templates');
                break;
            case "foods":
                navigate("/foods");
                break;
            case "settings":
                navigate("/settings");
                break;
            default:
                showError(t('unknown-error'));
        }
    }

    const handleLogout = async () => {
        try {
            // Call the backend logout endpoint
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, null, { withCredentials: true });

            // clear the app context
            setDietist(null);
            localStorage.removeItem('dietist');  // Remove from localStorage
            
            // Redirect the user to the login page
            navigate('/login');
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };

    return (
        <div
            id='sidebar'
            className={`sidebar ${isExpanded ? 'expanded' : ''}`}
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
        >
            <div className="logo" onClick={() => handleMenuClick("home")}>
                <FaBars className="menu-icon" />
                {isExpanded && <span className="logo-text">DietistPro</span>}
            </div>

            <hr className='sidebar-divider' />

            <ul className="menu-items">
                <li
                    className={`menu-item ${activeItem === 'home' ? 'active' : ''}`}
                    onClick={() => handleMenuClick('home')}
                >
                    <FaHome className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'home' ? 'active' : ''}`}>{t("Home")}</span>}
                </li>
                <li
                    className={`menu-item ${activeItem === 'calendar' ? 'active' : ''}`}
                    onClick={() => handleMenuClick('calendar')}
                >
                    <FaRegCalendarAlt className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'calendar' ? 'active' : ''}`}>{t("Calendar")}</span>}
                </li>
                <li
                    className={`menu-item ${activeItem === 'clients' ? 'active' : ''}`}
                    onClick={() => handleMenuClick('clients')}
                >
                    <PeopleIcon className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'clients' ? 'active' : ''}`}>{t("Clients")}</span>}
                </li>
                <li
                    className={`menu-item ${activeItem === 'templates' ? 'active' : ''}`}
                    onClick={() => handleMenuClick('templates')}
                >
                    <FaRegClone className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'templates' ? 'active' : ''}`}>{t('Templates')}</span>}
                </li>
                <li
                    className={`menu-item ${activeItem === 'foods' ? 'active' : ''}`}
                    onClick={() => handleMenuClick('foods')}
                >
                    <RestaurantIcon className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'foods' ? 'active' : ''}`}>{capitalizeFirst(t("foods-sidebar-text"))}</span>}
                </li>
                <li
                    className={`menu-item ${activeItem === 'settings' ? 'active' : ''}`}
                    onClick={() => handleMenuClick('settings')}
                >
                    <FaCog className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'settings' ? 'active' : ''}`}>{t("Settings")}</span>}
                </li>
                <li
                    id='sidebar-logout'
                    className={`menu-item ${activeItem === 'logout' ? 'active' : ''}`}
                    onClick={() => handleLogout()}
                >
                    <FaSignOutAlt className="menu-icon" />
                    {isExpanded && <span className={`menu-text ${activeItem === 'logout' ? 'active' : ''}`}>{t("Logout")}</span>}
                </li>
            </ul>
        </div>
    );
};

export default Sidebar;
